// src/sections/notifications/NotificationCard.jsx
// -----------------------------------------------------------------
// iOS frosted glass notification card. Renders any of the 16 CRM
// notification types as a glass morphism card with left accent bar,
// icon squircle, message body, snooze dropdown, and split action buttons.
//
// Design: matches NotificationListPage.jsx glass aesthetic
// - Frosted glass background with backdrop-filter blur
// - Read/unread opacity difference
// - Left accent bar with read/unread opacity
// - Snoozed badge, grouped count with layers icon
// - Split action bar: Mark read | Snooze | Primary action
// - cardEnter animation
// -----------------------------------------------------------------

import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, styled, keyframes } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { getNotificationType, NOTIFICATION_PALETTE } from './notificationTypes';

// -----------------------------------------------------------------
// Snooze preset options
// -----------------------------------------------------------------

const SNOOZE_PRESETS = [
  { label: '15 minutes', minutes: 15 },
  { label: '30 minutes', minutes: 30 },
  { label: '1 hour', minutes: 60 },
  { label: '2 hours', minutes: 120 },
  { divider: true },
  { label: 'Tomorrow 9am', minutes: null },
];

function getMinutesToTomorrow9am() {
  const now = new Date();
  const tomorrow9 = new Date(now);
  tomorrow9.setDate(tomorrow9.getDate() + 1);
  tomorrow9.setHours(9, 0, 0, 0);
  return Math.ceil((tomorrow9.getTime() - now.getTime()) / 60000);
}

// -----------------------------------------------------------------
// Animations
// -----------------------------------------------------------------

const cardEnter = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const iosPulse = keyframes`
  0%   { box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 0 0 0 rgba(255,86,48,0.25); }
  70%  { box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 0 0 8px rgba(255,86,48,0); }
  100% { box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 0 0 0 rgba(255,86,48,0); }
`;

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(4px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

// -----------------------------------------------------------------
// Styled components — iOS glass card
// -----------------------------------------------------------------

const GlassRoot = styled(Box, {
  shouldForwardProp: (p) => !['isUnread', 'isSnoozed', 'animDelay'].includes(p),
})(({ isUnread, isSnoozed, animDelay }) => ({
  position: 'relative',
  background: isUnread
    ? 'rgba(255, 255, 255, 0.82)'
    : 'rgba(255, 255, 255, 0.45)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
  borderRadius: 18,
  border: `0.5px solid ${isUnread ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)'}`,
  boxShadow: isUnread
    ? '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04), inset 0 0.5px 0 rgba(255,255,255,0.6)'
    : '0 2px 8px rgba(0,0,0,0.03)',
  overflow: 'hidden',
  cursor: 'pointer',
  // eslint-disable-next-line no-nested-ternary
  opacity: isSnoozed ? 0.6 : (isUnread ? 1 : 0.75),
  transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
  animation: `${cardEnter} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) ${animDelay || 0}s both`,
  '&:hover': {
    transform: 'scale(1.008)',
  },
}));

const IconSquircle = styled(Box, {
  shouldForwardProp: (p) => p !== 'pulse',
})(({ pulse }) => ({
  flexShrink: 0,
  width: 42,
  height: 42,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  ...(pulse && {
    animation: `${iosPulse} 2s ease-out infinite`,
  }),
}));

const SplitButton = styled('button', {
  shouldForwardProp: (p) => !['isAction', 'accentColour', 'hasBorderRight'].includes(p),
})(({ isAction, accentColour, hasBorderRight }) => ({
  flex: 1,
  fontFamily: "'Public Sans', sans-serif",
  fontSize: 13,
  fontWeight: isAction ? 700 : 600,
  padding: '10px 0',
  border: 'none',
  borderRight: hasBorderRight ? '0.5px solid rgba(0,0,0,0.05)' : 'none',
  cursor: 'pointer',
  background: 'transparent',
  color: isAction ? (accentColour || NOTIFICATION_PALETTE.primary.main) : '#919EAB',
  transition: 'all 0.15s',
  letterSpacing: '-0.1px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  '&:hover': {
    background: 'rgba(0,0,0,0.02)',
    ...((!isAction) && { color: '#637381' }),
  },
}));

const SnoozeDropdown = styled(Box)(() => ({
  position: 'absolute',
  bottom: 'calc(100% + 4px)',
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'rgba(255,255,255,0.95)',
  backdropFilter: 'blur(40px)',
  WebkitBackdropFilter: 'blur(40px)',
  borderRadius: 14,
  boxShadow: '0 20px 40px -4px rgba(145,158,171,0.24)',
  padding: 6,
  zIndex: 100,
  minWidth: 170,
  border: '0.5px solid rgba(255,255,255,0.5)',
  animation: `${fadeSlideUp} 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)`,
}));

const SnoozeOption = styled('button')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '9px 14px',
  border: 'none',
  background: 'none',
  width: '100%',
  fontFamily: "'Public Sans', sans-serif",
  fontSize: 13,
  fontWeight: 500,
  color: '#637381',
  cursor: 'pointer',
  borderRadius: 8,
  transition: 'background 0.1s',
  letterSpacing: '-0.1px',
  '&:hover': {
    background: '#F9FAFB',
    color: '#212B36',
  },
}));

// -----------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------

function getWaitingTime(timestamp) {
  if (!timestamp) return null;
  const diff = Date.now() - new Date(timestamp).getTime();
  if (diff < 0) return null;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Waiting less than a minute';
  if (mins < 60) return `Waiting ${mins} minute${mins === 1 ? '' : 's'}`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Waiting ${hrs} hour${hrs === 1 ? '' : 's'}`;
  const days = Math.floor(hrs / 24);
  return `Waiting ${days} day${days === 1 ? '' : 's'}`;
}

// -----------------------------------------------------------------
// Component
// -----------------------------------------------------------------

export default function NotificationCard({
  type,
  message,
  timeAgo,
  preview,
  practiceName,
  starRating,
  isRead,
  onAction,
  onDismiss,
  onMarkRead,
  priority,
  actionLabel,
  snoozable,
  snoozedUntil,
  groupedCount,
  onSnooze,
  timestamp,
  animDelay,
  sx,
  ...other
}) {
  const config = getNotificationType(type);
  const effectivePriority = priority || 'normal';
  const isUrgent = effectivePriority === 'urgent';
  const isUnread = !isRead;
  const isSnoozed = !!snoozedUntil;
  const accentColour = config.iconColor?.color || NOTIFICATION_PALETTE.grey[400];

  const [hovered, setHovered] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const snoozeRef = useRef(null);

  // Close snooze on outside click
  useEffect(() => {
    if (!snoozeOpen) return undefined;
    const handler = (e) => {
      if (snoozeRef.current && !snoozeRef.current.contains(e.target)) setSnoozeOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [snoozeOpen]);

  const waitingTime = useMemo(
    () => (isUrgent && isUnread ? getWaitingTime(timestamp) : null),
    [isUrgent, isUnread, timestamp]
  );

  const handleSnooze = (preset) => {
    setSnoozeOpen(false);
    if (!onSnooze) return;
    if (preset.label === 'Tomorrow 9am') {
      onSnooze(getMinutesToTomorrow9am());
    } else {
      onSnooze(preset.minutes);
    }
  };

  const hasGrouped = groupedCount > 1;
  const hasActionBar = actionLabel || snoozable || onMarkRead;

  // Iconify icon name mapping from config
  const ICON_MAP = {
    phone_callback: 'solar:phone-calling-rounded-bold-duotone',
    warning_amber: 'solar:danger-triangle-bold-duotone',
    person_off: 'solar:user-cross-bold-duotone',
    schedule: 'solar:calendar-bold-duotone',
    payment: 'solar:card-bold-duotone',
    person_add: 'solar:user-plus-bold-duotone',
    assignment_ind: 'solar:user-check-bold-duotone',
    task: 'solar:check-square-bold-duotone',
    mail: 'solar:letter-bold-duotone',
    comment: 'solar:chat-round-bold-duotone',
    restore: 'solar:history-bold-duotone',
    chat: 'ic:baseline-whatsapp',
    payments: 'solar:card-bold-duotone',
    medical_services: 'solar:medical-kit-bold-duotone',
    star: 'solar:star-bold-duotone',
    phone_in_talk: 'solar:phone-bold-duotone',
    phone: 'solar:phone-bold-duotone',
  };

  const iconName = ICON_MAP[config.icon] || `solar:${config.icon}-bold-duotone`;

  return (
    <GlassRoot
      isUnread={isUnread ? 1 : 0}
      isSnoozed={isSnoozed ? 1 : 0}
      animDelay={animDelay}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onAction?.(actionLabel || 'View')}
      sx={{
        ...(hovered && { transform: 'scale(1.008)' }),
        ...sx,
      }}
      {...other}
    >
      {/* Glass top highlight */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 20,
          right: 20,
          height: '0.5px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)',
        }}
      />

      {/* Left accent bar */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 8,
          bottom: 8,
          width: 3,
          borderRadius: 2,
          bgcolor: accentColour,
          opacity: isUnread ? 1 : 0.3,
        }}
      />

      {/* Main content */}
      <Box sx={{ display: 'flex', gap: '14px', p: '16px 16px 14px 18px' }}>
        {/* Icon squircle */}
        <IconSquircle
          pulse={isUrgent && isUnread ? 1 : 0}
          sx={{
            bgcolor: config.iconColor?.bg || NOTIFICATION_PALETTE.grey[200],
            color: config.iconColor?.color || NOTIFICATION_PALETTE.grey[600],
          }}
        >
          <Iconify icon={iconName} width={19} />
        </IconSquircle>

        {/* Body */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Top row: category + grouped count | snoozed badge + time + unread dot */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '3px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', letterSpacing: '-0.2px' }}
              >
                {config.category}
              </Typography>
              {isUrgent && isUnread && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    boxShadow: (theme) => `0 0 6px ${theme.palette.error.main}50`,
                  }}
                />
              )}
              {hasGrouped && (
                <Box
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: NOTIFICATION_PALETTE.grey[200],
                    color: NOTIFICATION_PALETTE.grey[600],
                    py: '1px',
                    px: '6px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                  }}
                >
                  <Iconify icon="solar:layers-bold-duotone" width={10} />
                  {groupedCount}
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {isSnoozed && (
                <Box
                  sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: NOTIFICATION_PALETTE.warning.dark,
                    bgcolor: NOTIFICATION_PALETTE.warning.lighter,
                    py: '2px',
                    px: '7px',
                    borderRadius: '6px',
                  }}
                >
                  Snoozed until{' '}
                  {new Date(snoozedUntil).toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Box>
              )}
              {timeAgo && (
                <Typography sx={{ fontSize: 12, color: 'text.disabled', fontWeight: 500, letterSpacing: '-0.1px' }}>
                  {timeAgo}
                </Typography>
              )}
              {isUnread && !isSnoozed && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: isUrgent ? 'error.main' : 'info.main',
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Practice name */}
          {practiceName && (
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: 'text.disabled', mb: '2px', letterSpacing: '-0.1px' }}
            >
              {practiceName}
            </Typography>
          )}

          {/* Description */}
          <Typography
            sx={{
              fontSize: 13.5,
              color: 'text.primary',
              lineHeight: 1.45,
              letterSpacing: '-0.1px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {message}
          </Typography>

          {/* Urgency indicator for urgent unread cards */}
          {waitingTime && (
            <Box
              sx={{
                mt: '4px',
                fontSize: 11,
                fontWeight: 700,
                color: 'error.main',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
              }}
            >
              <Iconify icon="mdi:timer-outline" width={13} />
              {waitingTime}
            </Box>
          )}

          {/* Star rating (Google Review) */}
          {config.hasStarRating && starRating > 0 && (
            <Box sx={{ display: 'flex', gap: '1px', mt: '4px' }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Iconify
                  key={i}
                  icon={i < starRating ? 'mdi:star' : 'mdi:star-outline'}
                  width={14}
                  sx={{ color: i < starRating ? '#FFAB00' : '#DFE3E8' }}
                />
              ))}
            </Box>
          )}

          {/* Preview block */}
          {config.hasPreview && preview && (
            <Box
              sx={{
                mt: '6px',
                p: '8px 10px',
                bgcolor: 'rgba(244,246,248,0.6)',
                borderRadius: '8px',
                fontSize: 12.5,
                color: 'text.secondary',
                lineHeight: 1.5,
                fontStyle: 'italic',
                borderLeft: '2px solid #DFE3E8',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {preview}
            </Box>
          )}
        </Box>
      </Box>

      {/* Action bar (split buttons) */}
      {hasActionBar && !isSnoozed && (
        <Box sx={{ borderTop: '0.5px solid rgba(0,0,0,0.05)', display: 'flex' }}>
          {/* Mark read / Dismiss */}
          <SplitButton
            hasBorderRight={1}
            onClick={(e) => {
              e.stopPropagation();
              if (isUnread && onMarkRead) {
                onMarkRead();
              } else {
                onDismiss?.();
              }
            }}
          >
            {isUnread ? 'Mark read' : 'Dismiss'}
          </SplitButton>

          {/* Snooze */}
          {snoozable && !isSnoozed && (
            <Box ref={snoozeRef} sx={{ flex: 1, position: 'relative' }}>
              <SplitButton
                hasBorderRight={actionLabel ? 1 : 0}
                accentColour={NOTIFICATION_PALETTE.warning.dark}
                isAction={1}
                onClick={(e) => {
                  e.stopPropagation();
                  setSnoozeOpen(!snoozeOpen);
                }}
                style={{ width: '100%' }}
              >
                <Iconify icon="mdi:snooze" width={14} />
                Snooze
              </SplitButton>

              {snoozeOpen && (
                <SnoozeDropdown>
                  {SNOOZE_PRESETS.map((opt, i) =>
                    opt.divider ? (
                      <Box
                        key={`div-${i}`}
                        sx={{ height: 1, bgcolor: NOTIFICATION_PALETTE.grey[200], my: '4px' }}
                      />
                    ) : (
                      <SnoozeOption
                        key={opt.label}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSnooze(opt);
                        }}
                      >
                        <Iconify icon="mdi:snooze" width={14} />
                        {opt.label}
                      </SnoozeOption>
                    )
                  )}
                </SnoozeDropdown>
              )}
            </Box>
          )}

          {/* Primary action */}
          {actionLabel && (
            <SplitButton
              isAction={1}
              accentColour={isUrgent ? NOTIFICATION_PALETTE.error.main : NOTIFICATION_PALETTE.primary.main}
              onClick={(e) => {
                e.stopPropagation();
                onAction?.(actionLabel);
              }}
            >
              {actionLabel}
            </SplitButton>
          )}
        </Box>
      )}

      {/* Legacy fallback: config-based action buttons when no action_label */}
      {!actionLabel && !snoozable && !onMarkRead && !isSnoozed && config.actions && (
        <Box sx={{ borderTop: '0.5px solid rgba(0,0,0,0.05)', display: 'flex' }}>
          {config.actions.map((action, i) => (
            <SplitButton
              key={i}
              hasBorderRight={i < config.actions.length - 1 ? 1 : 0}
              isAction={action.variant === 'primary' || action.variant === 'urgent' || action.variant === 'whatsapp' ? 1 : 0}
              accentColour={
                // eslint-disable-next-line no-nested-ternary
                action.variant === 'urgent'
                  ? NOTIFICATION_PALETTE.error.main
                  : action.variant === 'whatsapp'
                    ? NOTIFICATION_PALETTE.whatsapp.dark
                    : NOTIFICATION_PALETTE.primary.main
              }
              onClick={(e) => {
                e.stopPropagation();
                if (action.label === 'Dismiss') {
                  onDismiss?.();
                } else {
                  onAction?.(action.label);
                }
              }}
            >
              {action.label}
            </SplitButton>
          ))}
        </Box>
      )}
    </GlassRoot>
  );
}

NotificationCard.propTypes = {
  type: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  timeAgo: PropTypes.string,
  preview: PropTypes.string,
  chips: PropTypes.array,
  practiceName: PropTypes.string,
  starRating: PropTypes.number,
  isRead: PropTypes.bool,
  onAction: PropTypes.func,
  onDismiss: PropTypes.func,
  onMarkRead: PropTypes.func,
  priority: PropTypes.oneOf(['urgent', 'normal', 'low']),
  actionLabel: PropTypes.string,
  snoozable: PropTypes.bool,
  snoozedUntil: PropTypes.string,
  groupedCount: PropTypes.number,
  onSnooze: PropTypes.func,
  timestamp: PropTypes.string,
  animDelay: PropTypes.number,
  sx: PropTypes.object,
};
